import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Camera, Upload, Trash2, Pencil, X, Sparkles, 
  Image, AlertCircle, Eye, Loader2, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { todayLocal } from '../utils/dateUtils';

const BODY_AREAS = [
  { value: 'face', label: 'Face' },
  { value: 'arm', label: 'Arm' },
  { value: 'leg', label: 'Leg' },
  { value: 'torso', label: 'Torso/Chest' },
  { value: 'hands', label: 'Hands' },
  { value: 'feet', label: 'Feet' },
  { value: 'back', label: 'Back' },
  { value: 'neck', label: 'Neck' },
  { value: 'other', label: 'Other' }
];

const SYMPTOM_TYPES = [
  { value: 'rash', label: 'Rash' },
  { value: 'swelling', label: 'Swelling' },
  { value: 'redness', label: 'Redness' },
  { value: 'bruising', label: 'Bruising' },
  { value: 'hives', label: 'Hives' },
  { value: 'eczema', label: 'Eczema' },
  { value: 'wound', label: 'Wound/Cut' },
  { value: 'other', label: 'Other' }
];

const SymptomPhotoLog = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    date: todayLocal(),
    body_area: '',
    symptom_type: '',
    image_data: null,
    image_preview: null,
    notes: ''
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/symptom-photos?days=90`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching photo entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1];
      setFormData(prev => ({
        ...prev,
        image_data: base64,
        image_preview: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image_data) {
      toast.error('Please select an image');
      return;
    }
    
    if (!formData.body_area || !formData.symptom_type) {
      toast.error('Please select body area and symptom type');
      return;
    }

    setUploading(true);
    try {
      await axios.post(`${API}/symptom-photos`, {
        date: formData.date,
        body_area: formData.body_area,
        symptom_type: formData.symptom_type,
        image_data: formData.image_data,
        notes: formData.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Photo saved with AI analysis!');
      resetForm();
      fetchEntries();
    } catch (error) {
      console.error('Error saving photo:', error);
      toast.error('Failed to save photo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this photo entry?')) return;
    
    try {
      await axios.delete(`${API}/symptom-photos/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      setSelectedEntry(null);
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const viewEntryDetail = async (entry) => {
    try {
      const response = await axios.get(`${API}/symptom-photos/${entry.id}?include_image=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditMode(false);
      setSelectedEntry(response.data);
    } catch (error) {
      console.error('Error fetching entry detail:', error);
      toast.error('Failed to load photo');
    }
  };

  const startEditEntry = () => {
    setEditData({
      date: selectedEntry.date,
      body_area: selectedEntry.body_area,
      symptom_type: selectedEntry.symptom_type,
      notes: selectedEntry.notes || ''
    });
    setEditMode(true);
  };

  const saveEntryEdit = async () => {
    try {
      await axios.put(`${API}/symptom-photos/${selectedEntry.id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo details updated');
      setSelectedEntry({ ...selectedEntry, ...editData });
      setEditMode(false);
      fetchEntries();
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error('Failed to update details');
    }
  };

  const resetForm = () => {
    setFormData({
      date: todayLocal(),
      body_area: '',
      symptom_type: '',
      image_data: null,
      image_preview: null,
      notes: ''
    });
    setShowForm(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getSeverityColor = (severity) => {
    if (!severity) return 'bg-gray-100 text-gray-600';
    if (severity === 'mild') return 'bg-green-100 text-green-700';
    if (severity === 'moderate') return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div className="space-y-8" data-testid="symptom-photo-log">
      <Button 
        onClick={() => navigate('/dashboard')} 
        variant="ghost" 
        className="text-primary hover:text-primary/80"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Dashboard
      </Button>

      {/* Header */}
      <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl p-8">
        <div className="flex items-center gap-4 mb-2">
          <Camera className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
          <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">
            Symptom Photo Log
          </h1>
        </div>
        <p className="text-[#57534E]">
          Photograph visible symptoms for tracking changes and AI-powered analysis
        </p>
      </div>

      {/* Selected Entry Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-[#2D2D2D]">
                  {formatDate(selectedEntry.date)} - {selectedEntry.symptom_type}
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setSelectedEntry(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {selectedEntry.image_data && (
                <img 
                  src={`data:image/jpeg;base64,${selectedEntry.image_data}`}
                  alt="Symptom photo"
                  className="w-full rounded-lg mb-4"
                />
              )}
              
              <div className="space-y-3">
                {editMode ? (
                  <div className="space-y-4" data-testid="photo-edit-form">
                    <div>
                      <Label className="text-[#2D2D2D] font-medium">Date</Label>
                      <Input
                        type="date"
                        value={editData.date}
                        onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                        className="mt-1"
                        data-testid="photo-edit-date-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#2D2D2D] font-medium mb-2 block">Body Area</Label>
                      <div className="flex flex-wrap gap-2">
                        {BODY_AREAS.map(({ value, label }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setEditData({ ...editData, body_area: value })}
                            className={`px-3 py-1.5 rounded-full border-2 text-sm transition-all ${
                              editData.body_area === value
                                ? 'border-amber-500 bg-amber-50 text-amber-700'
                                : 'border-stone-200 hover:border-amber-300 text-[#57534E]'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#2D2D2D] font-medium mb-2 block">Symptom Type</Label>
                      <div className="flex flex-wrap gap-2">
                        {SYMPTOM_TYPES.map(({ value, label }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setEditData({ ...editData, symptom_type: value })}
                            className={`px-3 py-1.5 rounded-full border-2 text-sm transition-all ${
                              editData.symptom_type === value
                                ? 'border-amber-500 bg-amber-50 text-amber-700'
                                : 'border-stone-200 hover:border-amber-300 text-[#57534E]'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#2D2D2D] font-medium">Notes</Label>
                      <Textarea
                        value={editData.notes}
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                        className="mt-1"
                        rows={3}
                        data-testid="photo-edit-notes-input"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEditMode(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={saveEntryEdit}
                        className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="photo-edit-save-btn"
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                <>
                <div className="flex gap-2">
                  <span className="text-sm bg-stone-100 text-stone-600 px-3 py-1 rounded-full capitalize">
                    {selectedEntry.body_area}
                  </span>
                  <span className="text-sm bg-amber-100 text-amber-700 px-3 py-1 rounded-full capitalize">
                    {selectedEntry.symptom_type}
                  </span>
                  {selectedEntry.severity && (
                    <span className={`text-sm px-3 py-1 rounded-full capitalize ${getSeverityColor(selectedEntry.severity)}`}>
                      {selectedEntry.severity}
                    </span>
                  )}
                </div>
                
                {selectedEntry.ai_analysis && (
                  <div className="bg-amber-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-amber-700 flex items-center gap-1 mb-2">
                      <Sparkles className="w-4 h-4" />
                      AI Analysis
                    </p>
                    <p className="text-sm text-[#57534E] whitespace-pre-wrap">{selectedEntry.ai_analysis}</p>
                  </div>
                )}
                
                {selectedEntry.notes && (
                  <div>
                    <p className="text-sm font-medium text-[#2D2D2D]">Notes:</p>
                    <p className="text-sm text-[#57534E]">{selectedEntry.notes}</p>
                  </div>
                )}
                
                <Button
                  onClick={startEditEntry}
                  variant="outline"
                  className="w-full text-amber-700 border-amber-200 hover:bg-amber-50"
                  data-testid="photo-edit-details-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Details
                </Button>
                <Button
                  onClick={() => handleDelete(selectedEntry.id)}
                  variant="outline"
                  className="w-full text-red-500 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Photo
                </Button>
                </>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Add Photo Button */}
      {!showForm && (
        <Button
          onClick={() => setShowForm(true)}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-6"
          data-testid="add-photo-btn"
        >
          <Camera className="w-5 h-5 mr-2" />
          Add Symptom Photo
        </Button>
      )}

      {/* Upload Form */}
      {showForm && (
        <Card className="p-6 border-2 border-amber-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-[#2D2D2D] flex items-center gap-2">
              <Camera className="w-5 h-5 text-amber-600" />
              Upload Photo
            </h2>
            <Button variant="ghost" size="sm" onClick={resetForm}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Image Upload */}
            <div>
              <Label className="text-[#2D2D2D] font-medium mb-3 block">Photo</Label>
              
              {formData.image_preview ? (
                <div className="relative">
                  <img 
                    src={formData.image_preview} 
                    alt="Preview" 
                    className="w-full max-h-64 object-contain rounded-lg border"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 bg-white/80 hover:bg-white"
                    onClick={() => setFormData(prev => ({ ...prev, image_data: null, image_preview: null }))}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-stone-300 rounded-xl p-8 text-center cursor-pointer hover:border-amber-400 transition-colors"
                >
                  <Upload className="w-10 h-10 mx-auto mb-3 text-stone-400" />
                  <p className="text-[#57534E]">Click to upload or drag and drop</p>
                  <p className="text-sm text-[#A8A29E]">PNG, JPG up to 10MB</p>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="photo-input"
              />
            </div>

            {/* Date */}
            <div>
              <Label className="text-[#2D2D2D] font-medium">Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-1"
                required
                data-testid="photo-date-input"
              />
            </div>

            {/* Body Area */}
            <div>
              <Label className="text-[#2D2D2D] font-medium mb-3 block">Body Area</Label>
              <div className="flex flex-wrap gap-2">
                {BODY_AREAS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData({ ...formData, body_area: value })}
                    className={`px-4 py-2 rounded-full border-2 transition-all ${
                      formData.body_area === value
                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-stone-200 hover:border-amber-300 text-[#57534E]'
                    }`}
                    data-testid={`body-area-${value}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Symptom Type */}
            <div>
              <Label className="text-[#2D2D2D] font-medium mb-3 block">Symptom Type</Label>
              <div className="flex flex-wrap gap-2">
                {SYMPTOM_TYPES.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData({ ...formData, symptom_type: value })}
                    className={`px-4 py-2 rounded-full border-2 transition-all ${
                      formData.symptom_type === value
                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-stone-200 hover:border-amber-300 text-[#57534E]'
                    }`}
                    data-testid={`symptom-type-${value}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-[#2D2D2D] font-medium">Notes (optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional observations about this symptom..."
                className="mt-1"
                rows={3}
                data-testid="photo-notes-input"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={uploading || !formData.image_data}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="photo-submit-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Save with AI Analysis
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Info Card */}
      <Card className="p-4 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-semibold text-[#2D2D2D]">Photo Documentation Tips</p>
            <ul className="text-sm text-[#57534E] list-disc ml-4 mt-1 space-y-1">
              <li>Use consistent lighting for accurate comparison</li>
              <li>Include a size reference (coin, ruler) when possible</li>
              <li>Take photos from the same angle each time</li>
              <li>Photos are analyzed by AI for documentation purposes only - not diagnosis</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Photo Gallery */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-[#2D2D2D]">Photo History</h2>
        
        {loading ? (
          <p className="text-center text-[#57534E] py-8">Loading...</p>
        ) : entries.length === 0 ? (
          <Card className="p-8 text-center">
            <Image className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p className="text-[#57534E]">No photos yet. Start documenting symptoms!</p>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {entries.map((entry) => (
              <Card 
                key={entry.id} 
                className="p-3 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => viewEntryDetail(entry)}
                data-testid={`photo-entry-${entry.id}`}
              >
                <div className="aspect-square bg-stone-100 rounded-lg mb-2 flex items-center justify-center">
                  <div className="text-center">
                    <Camera className="w-8 h-8 mx-auto text-stone-400 mb-1" />
                    <p className="text-xs text-[#A8A29E]">Tap to view</p>
                  </div>
                </div>
                
                <p className="text-sm font-semibold text-[#2D2D2D]">{formatDate(entry.date)}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  <span className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded capitalize">
                    {entry.body_area}
                  </span>
                  {entry.severity && (
                    <span className={`text-xs px-2 py-0.5 rounded capitalize ${getSeverityColor(entry.severity)}`}>
                      {entry.severity}
                    </span>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SymptomPhotoLog;
